import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, ArrowLeft } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Emblem } from "@/components/Logo";
import { Loader, Badge } from "@/components/kit";

export default function Pricing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [plans, setPlans] = useState(null);
  const [cycle, setCycle] = useState("monthly");
  const [busy, setBusy] = useState(null);

  useEffect(() => { api.get("/plans").then(({ data }) => setPlans(data.plans)).catch(() => setPlans([])); }, []);

  const checkout = async (plan) => {
    if (!user) return nav("/signup");
    setBusy(plan.id);
    try {
      const lookup = cycle === "monthly" ? plan.lookup_monthly : plan.lookup_yearly;
      const { data } = await api.post("/payments/checkout", { lookup_key: lookup, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiErr(e)); setBusy(null); }
  };

  if (!plans) return <div className="min-h-screen bg-ink"><Loader /></div>;
  return (
    <div className="min-h-screen bg-ink grain">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-12">
        <div className="flex items-center justify-between mb-10">
          <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2 text-white/60 hover:text-gold text-sm"><ArrowLeft size={16} /> Back</Link>
          <Link to="/" className="flex items-center gap-2.5"><Emblem className="h-8 w-8" /><span className="font-serif text-lg gold-text tracking-wider">CHEF PLATED</span></Link>
        </div>
        <div className="text-center mb-10">
          <p className="label-eyebrow mb-3">Membership</p>
          <h1 className="font-serif text-5xl md:text-6xl">Choose your station</h1>
          <div className="inline-flex mt-8 p-1 rounded-sm border border-white/10 bg-surface">
            {["monthly", "yearly"].map((c) => (
              <button key={c} onClick={() => setCycle(c)} data-testid={`cycle-${c}`}
                className={`px-6 py-2.5 rounded-sm text-sm capitalize transition-colors ${cycle === c ? "bg-gold text-black font-medium" : "text-white/60"}`}>{c} {c === "yearly" && "· save 2mo"}</button>
            ))}
          </div>
        </div>
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
          {plans.map((p) => {
            const price = cycle === "monthly" ? p.monthly : p.yearly;
            const featured = p.id === "executive_chef";
            const current = user?.plan === p.id;
            return (
              <div key={p.id} data-testid={`plan-${p.id}`} className={`matte-card p-7 flex flex-col ${featured ? "gold-border relative" : ""}`}>
                {featured && <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-gold text-black text-[10px] uppercase tracking-widest rounded-sm">Most Popular</span>}
                <h3 className="font-serif text-3xl">{p.name}</h3>
                <p className="text-white/45 text-sm mt-1 h-10">{p.tagline}</p>
                <div className="mt-4 mb-6">
                  <span className="font-mono text-4xl gold-text">${price}</span>
                  <span className="text-white/40 text-sm">/{cycle === "monthly" ? "mo" : "yr"}</span>
                </div>
                {current ? <Badge tone="green">Current plan</Badge> : (
                  <button onClick={() => checkout(p)} disabled={busy === p.id} data-testid={`subscribe-${p.id}`}
                    className={`w-full py-3.5 text-xs uppercase tracking-widest rounded-sm ${featured ? "btn-gold" : "btn-outline-gold"}`}>
                    {busy === p.id ? "Redirecting…" : "Subscribe"}
                  </button>
                )}
                <ul className="mt-6 space-y-2.5 text-sm">
                  {p.features.map((f, i) => <li key={i} className="flex gap-2.5 text-white/70"><Check size={16} className="text-gold shrink-0 mt-0.5" /> {f}</li>)}
                </ul>
              </div>
            );
          })}
        </div>
        <p className="text-center text-white/30 text-sm mt-8">Test mode · use card 4242 4242 4242 4242 with any future expiry and CVC.</p>
      </div>
    </div>
  );
}
